"use client";
import { useState, useEffect, useMemo } from "react";
import { useRouter } from "next/navigation";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent
} from "@/components/ui/accordion";
import { useDispatch, useSelector } from "react-redux";
import { fetchCustomerConfig } from "../config/redux/slices/ConfigSlice";
import {
  Shield, Lock, Eye, Database, UserCheck, Mail, Phone, MapPin,ArrowLeft ,
  FileText, AlertCircle, CheckCircle, ChevronDown, Sparkles, Calendar
} from "lucide-react";

const iconMapping = {
  'Information We Collect': Database,
  'How We Use Your Information': Eye,
  'Data Security': Lock,
  'Sharing Your Information': UserCheck,
  'Your Rights': Shield,
  'Cookies': FileText,
  'Third-Party Links': AlertCircle,
  'Changes to Privacy Policy': FileText,
  'Contact Us': Mail,
};

const colorMapping = {
  'Information We Collect': { bg: 'bg-blue-50', icon: 'text-blue-600', gradient: 'from-blue-500 to-blue-600' },
  'How We Use Your Information': { bg: 'bg-green-50', icon: 'text-green-600', gradient: 'from-green-500 to-green-600' },
  'Data Security': { bg: 'bg-purple-50', icon: 'text-purple-600', gradient: 'from-purple-500 to-purple-600' },
  'Sharing Your Information': { bg: 'bg-orange-50', icon: 'text-orange-600', gradient: 'from-orange-500 to-orange-600' },
  'Your Rights': { bg: 'bg-teal-50', icon: 'text-teal-600', gradient: 'from-teal-500 to-teal-600' },
  'Cookies': { bg: 'bg-pink-50', icon: 'text-pink-600', gradient: 'from-pink-500 to-pink-600' },
  'Third-Party Links': { bg: 'bg-red-50', icon: 'text-red-600', gradient: 'from-red-500 to-red-600' },
  'Changes to Privacy Policy': { bg: 'bg-indigo-50', icon: 'text-indigo-600', gradient: 'from-indigo-500 to-indigo-600' },
  'Contact Us': { bg: 'bg-cyan-50', icon: 'text-cyan-600', gradient: 'from-cyan-500 to-cyan-600' },
};

function PrivacySkeleton() {
  return (
    <div className="flex flex-col min-h-screen max-w-7xl mt-24 w-full mx-auto px-4">
      <main className="flex-1">
        {/* Header Skeleton */}
        <div className="text-center mb-12 animate-pulse">
          <div className="h-8 w-32 mx-auto rounded-full bg-gray-200 mb-4" />
          <div className="h-12 w-96 mx-auto rounded-lg bg-gray-300 mb-4" />
          <div className="h-6 w-2/3 mx-auto rounded-lg bg-gray-200" />
        </div>

        {/* Accordion Skeleton */}
        <div className="max-w-5xl mx-auto space-y-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="border border-gray-200 rounded-2xl overflow-hidden bg-white shadow-sm animate-pulse"
            >
              <div className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gray-200" />
                <div className="flex-1">
                  <div className="h-6 w-3/4 bg-gray-300 rounded" />
                </div>
                <div className="w-6 h-6 rounded bg-gray-200" />
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

export default function Privacy() {
  const router = useRouter();
  const dispatch = useDispatch();
  const config = useSelector((state) => state.config.config);
  const isLoading = useSelector((state) => state.config.isLoading);

  useEffect(() => {
    dispatch(fetchCustomerConfig());
  }, [dispatch]);

  // Contact pattern extraction
  const emailRegex = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i;
  const phoneRegex = /(\+?\d{1,3}[- ]?)?\d{10,12}/;

  const sections = useMemo(() => {
    return (
      config?.privacyPolicies?.map((item, index) => {
        const isContactUs = item.title.toLowerCase().includes("contact");
        const Icon = iconMapping[item.title] || Shield;
        const colors = colorMapping[item.title] || {
          bg: 'bg-blue-50',
          icon: 'text-blue-600',
          gradient: 'from-blue-500 to-blue-600'
        };

        return {
          id: `section-${index}`,
          title: item.title,
          icon: Icon,
          colors: colors,
          content: (
            <div className="space-y-4">
              {item.description?.map((paragraph, pIndex) => {
                // Special rendering for contact info
                if (isContactUs) {
                  if (emailRegex.test(paragraph)) {
                    const email = paragraph.match(emailRegex)[0];
                    const text = paragraph.replace(email, "");
                    return (
                      <div key={pIndex} className="flex items-start gap-3 bg-blue-50 p-4 rounded-xl">
                        <Mail className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-gray-700 leading-relaxed mb-1">{text}</p>
                          <a
                            href={`mailto:${email}`}
                            className="text-[#00489b] font-semibold hover:underline"
                          >
                            {email}
                          </a>
                        </div>
                      </div>
                    );
                  }
                  if (phoneRegex.test(paragraph)) {
                    const phone = paragraph.match(phoneRegex)[0];
                    const text = paragraph.replace(phoneRegex, "");
                    return (
                      <div key={pIndex} className="flex items-start gap-3 bg-green-50 p-4 rounded-xl">
                        <Phone className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-gray-700 leading-relaxed mb-1">{text}</p>
                          <a
                            href={`tel:${phone}`}
                            className="text-[#00489b] font-semibold hover:underline"
                          >
                            {phone}
                          </a>
                        </div>
                      </div>
                    );
                  }
                }

                // Standard paragraph
                return (
                  <div key={pIndex} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700 leading-relaxed">
                      {paragraph}
                    </p>
                  </div>
                );
              })}
            </div>
          ),
        };
      }) || []
    );
  }, [config]);

  if (isLoading) return <PrivacySkeleton />;

  return (
    <div className="flex flex-col min-h-screen max-w-7xl mt-24 w-full mx-auto px-4 bg-gradient-to-b from-white via-gray-50 to-white">
      <main className="flex-1 py-12">
        <div className="max-w-5xl mx-auto mb-6">
          <button
            onClick={() => router.back()}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>

        {/* Hero Header */}
        <div className="text-center mb-16">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-[#00489b]/10 px-4 py-2 rounded-full mb-6">
            <Lock className="w-5 h-5 text-[#00489b]" />
            <span className="text-[#00489b] font-semibold text-sm">Your Privacy Matters</span>
          </div>

          {/* Main Heading */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 mb-4">
            Privacy Policy
          </h1>

          {/* Subheading */}
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            We are committed to protecting your personal information and your right to privacy. Learn how we collect, use, and safeguard your data.
          </p>

          {/* Last Updated */}
          <div className="mt-6 inline-flex items-center gap-2 text-sm text-gray-500">
            <Calendar className="w-4 h-4" />
            <span>Last updated: January 2025</span>
          </div>
        </div>

        <div className="max-w-5xl mx-auto mb-10">
          <div className="rounded-2xl border border-[#dbe8ff] bg-white p-5 md:p-6 shadow-sm">
            <p className="text-[20px] font-bold text-gray-900 mb-2">Quick summary</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <p className="text-[14px] text-gray-600">We collect only the data needed to deliver and improve services.</p>
              <p className="text-[14px] text-gray-600">Payment and account data are handled using secure, standard practices.</p>
              <p className="text-[14px] text-gray-600">You can contact us anytime for access, correction, or privacy concerns.</p>
            </div>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="max-w-5xl mx-auto mb-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Shield, label: "Secure Data", color: "text-blue-600", bg: "bg-blue-50" },
              { icon: Lock, label: "Encrypted", color: "text-green-600", bg: "bg-green-50" },
              { icon: Eye, label: "Transparent", color: "text-purple-600", bg: "bg-purple-50" },
              { icon: UserCheck, label: "Your Control", color: "text-orange-600", bg: "bg-orange-50" },
            ].map((badge, index) => {
              const Icon = badge.icon;
              return (
                <div
                  key={index}
                  className={`${badge.bg} rounded-xl p-4 flex flex-col items-center text-center`}
                >
                  <Icon className={`w-6 h-6 ${badge.color} mb-2`} />
                  <span className={`text-sm font-semibold ${badge.color}`}>
                    {badge.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Accordion */}
        <div className="max-w-5xl mx-auto">
          <Accordion type="multiple" className="space-y-4">
            {sections.map((section) => {
              const IconComp = section.icon;
              const colors = section.colors;

              return (
                <AccordionItem
                  key={section.id}
                  value={section.id}
                  className="border border-gray-200 rounded-2xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-all duration-300"
                >
                  <AccordionTrigger className="cursor-pointer p-6 hover:bg-gray-50/50 transition-colors [&[data-state=open]]:bg-gray-50">
                    <div className="flex w-full items-center gap-4">
                      {/* Icon */}
                      <div className={`${colors.bg} w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0`}>
                        <IconComp className={`w-6 h-6 ${colors.icon}`} />
                      </div>

                      {/* Title */}
                      <div className="flex-1 text-left">
                        <h3 className="text-lg md:text-xl font-bold text-gray-900">
                          {section.title}
                        </h3>
                      </div>

                      {/* Chevron */}
                      {/* <ChevronDown className="w-5 h-5 text-gray-400 transition-transform duration-300 [&[data-state=open]]:rotate-180" /> */}
                    </div>
                  </AccordionTrigger>

                  <AccordionContent className="px-6 pb-6 pt-2">
                    <div className="pl-16">
                      {section.content}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>

        <div className="mt-16 max-w-5xl mx-auto">
          <div className="bg-gradient-to-r from-[#0b4f9b] to-[#0d63bf] rounded-2xl p-8 md:p-10 text-center shadow-xl">
            <Sparkles className="w-10 h-10 text-white mx-auto mb-3" />
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
              Questions about privacy?
            </h3>
            <p className="text-blue-100 text-[16px] mb-6 max-w-2xl mx-auto">
              Reach out to support, or use the app for a more streamlined and transparent booking experience.
            </p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <a
                href="/contact-sortkaro"
                className="h-11 px-5 rounded-xl bg-white text-[#0b4f9b] text-[14px] font-semibold inline-flex items-center justify-center hover:bg-blue-50"
              >
                Contact Support
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.sortkaro.user&hl=en_IN"
                target="_blank"
                rel="noopener noreferrer"
                className="h-11 px-5 rounded-xl border border-white/60 text-white text-[14px] font-semibold inline-flex items-center justify-center hover:bg-white/10"
              >
                Get App
              </a>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
